/**
 * Analytics abstraction layer.
 *
 * Call sites use `useAnalytics()` which returns provider-agnostic methods.
 * The actual delivery (PostHog, Mixpanel, etc.) is handled by swapping
 * the AnalyticsProvider implementation passed to `initAnalytics()`.
 *
 * This is the Strategy pattern — same idea as Android's LogHandler interface.
 */

// ── Provider interface ──────────────────────────────────────────────────────

export interface AnalyticsProvider {
  init(): void
  capture(event: string, properties?: Record<string, unknown>): void
  identify(userId: string): void
  reset(): void
}

// ── Active provider ─────────────────────────────────────────────────────────

let provider: AnalyticsProvider | null = null

/**
 * Initialise analytics with a given provider.
 * Call once at app startup (e.g. in main.ts).
 */
export function initAnalytics(analyticsProvider: AnalyticsProvider) {
  provider = analyticsProvider
  provider.init()
}

// ── Composable (public API — unchanged for all call sites) ──────────────────

export function useAnalytics() {
  function capture(event: string, properties?: Record<string, unknown>) {
    provider?.capture(event, properties)
  }

  function identify(userId: string) {
    provider?.identify(userId)
  }

  function reset() {
    provider?.reset()
  }

  function capturePageView() {
    provider?.capture('$pageview')
  }

  return { capture, identify, reset, capturePageView }
}
